import { useCallback, useMemo, useState } from "react";
import { NewsItem } from "../interfaces/NewsItem";
import { markNewsAsViewed, getViewedNewsIds } from "../utils/newsStorage";
import { newsClient } from "@/shared/api/clients";

export const useNewsModal = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [dismissedMaintenanceIds, setDismissedMaintenanceIds] = useState<string[]>(getViewedNewsIds);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Separate maintenance items from other news
  const maintenanceItems = useMemo(
    () =>
      newsItems.filter(
        (item) =>
          item.category === "MAINTENANCE" &&
          !dismissedMaintenanceIds.includes(item.newsId)
      ),
    [newsItems, dismissedMaintenanceIds]
  );

  const nonMaintenanceItems = useMemo(
    () => newsItems.filter((item) => item.category !== "MAINTENANCE"),
    [newsItems]
  );

  const fetchNews = useCallback(async () => {
    if (!newsClient.defaults.baseURL) {
      console.warn("NEWS_API_BASE_URL is not defined; skipping news fetch.");
      return;
    }

    setLoading(true);
    try {
      const response = await newsClient.get<NewsItem>("/news");
      const latestNews = response.data;

      if (!latestNews) {
        setNewsItems([]);
        return;
      }

      setNewsItems([latestNews]);

      const seenIds = getViewedNewsIds();
      if (seenIds.includes(latestNews.newsId)) {
        return;
      }
      // Only open modal for non-maintenance items
      if (latestNews.category !== "MAINTENANCE") {
        setOpen(true);
      }
    } catch (error) {
      console.error("Failed to fetch news items", error);
      setNewsItems([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    // Only mark non-maintenance items as viewed when closing the modal
    const itemsToMark = newsItems.filter(
      (item) => item.category !== "MAINTENANCE"
    );
    if (itemsToMark.length > 0) {
      markNewsAsViewed(itemsToMark.map((item) => item.newsId));
    }
    setOpen(false);
  }, [newsItems]);

  const dismissMaintenanceItem = useCallback((newsId: string) => {
    setDismissedMaintenanceIds((prev) => [...prev, newsId]);
    markNewsAsViewed([newsId]);
  }, []);

  return {
    newsItems: nonMaintenanceItems,
    maintenanceItems,
    loading,
    open,
    fetchNews,
    handleClose,
    dismissMaintenanceItem,
  };
};
